import { useEffect, useState } from "react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackButton from "../../../components/BackButton";
import InputField from "../../../components/InputField";
import AuthCard from "../../../components/AuthCard";
import Button from "../../../components/Button";
import FormGroup from "../../../components/FormGroup";
import RequiredLabel from "../../../components/RequiredLabel";
import { verifyAndRouteEmailUser, sendOtp, OTP_TYPE } from "./services/authServices";
import { showNotification } from "../../../redux/slices/notificationSlice";
import ErrorText from "../../../components/ErrorText";

export default function VerifyEmail() {
  const email = useSelector((state) => state.auth.email); // Get email from Redux
  const [codeInput, setCodeInput] = useState("");
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isCodeComplete = codeInput.length === 4;
  const requiredReason = t("pleaseCompleteRequiredFields", {
    defaultValue: "Please complete all required fields correctly.",
  });

  useEffect(() => {
    if (resendCooldown === 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (!isCodeComplete || isVerifying) return;
    setIsVerifying(true);
    setError("");
    try {
      const result = await verifyAndRouteEmailUser(email, codeInput);

      if (result.isExistingUser) {
        dispatch(
          showNotification({ message: t('emailVerified'), type: "success" })
        );
        navigate("/dashboard");
      } else {
        dispatch(
          showNotification({ message: t('emailVerified'), type: "success" })
        );
        navigate("/auth/set-password");
      }
    } catch (err) {
      setError(t('invalidOrExpiredOtp'));
      dispatch(
        showNotification({ message: t('invalidOrExpiredOtp'), type: "error" })
      );
      setResendCooldown(0); // Let user resend immediately after a failed attempt
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setIsResending(true);
    setError("");
    try {
      await sendOtp(email, OTP_TYPE.EMAIL);
      dispatch(
        showNotification({
          message: t("verificationEmailSent"),
          type: "success",
        })
      );
      setResendCooldown(60);
    } catch (err) {
      dispatch(
        showNotification({
          message: t("sendVerificationCodeFail"),
          type: "error",
        })
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 animate-fade-in">
      <AuthCard>
        <BackButton />
        <h2 className="sub-header mb-4">{t("verifyYourEmail")}</h2>
        <p>
          {t("enterCodePrompt")}{" "}
          <span className="font-semibold text-red-600">{email}</span>
        </p>
        <h2 className="sub-header mb-4">{t("enterVerificationCode")}</h2>
        <FormGroup>
          <RequiredLabel htmlFor="email-code" className="mb-2">
            {t("enterVerificationCode")}
          </RequiredLabel>
          <InputField
            id="email-code"
            type="text"
            maxLength={4}
            value={codeInput}
            onChange={(e) =>
              setCodeInput(e.target.value.replace(/[^0-9]/g, ""))
            }
            placeholder="1234"
            className="text-center"
            required
          />
        </FormGroup>
        <ErrorText>{error}</ErrorText>
        <Button
          onClick={handleVerify}
          className="w-full mt-4"
          disabled={!isCodeComplete || isVerifying}
          disabledReason={!isCodeComplete ? requiredReason : ""}
          loading={isVerifying}
          loadingLabel={t("loading")}
        >
          {t("verify")}
        </Button>
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <span>
            {resendCooldown > 0
              ? t("resendAvailableIn", { seconds: resendCooldown })
              : t("didntReceiveCode")}
          </span>
          <Button
            variant="secondary"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending || isVerifying}
            className="h-8 px-3 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            loading={isResending}
            loadingLabel={t("resendingCode")}
          >
            {isResending
              ? t("resendingCode")
              : resendCooldown > 0
                ? t("resendWaiting")
                : t("resendCode")}
          </Button>
        </div>
      </AuthCard>
    </div>
  );
}
